import {
  getRoomModel,
  getPassengerModel
} from './models';
import { getNestedStateObject } from '@/utils';
import { currentProfileCache } from '@/modules/utils/currentProfileCache.js';
import { ensureCampaignParamsOnCheckoutQuery } from '@/modules/utm-persistence';

const updateStepUrl = (state) => {
  const stepNames = {
    'addon': 'holiday',
    'flights': 'holiday',
    'travellers': 'holiday',
    'excursions': 'holiday',
    'passengerDetails': 'details',
    'payment': 'payment',
    'booking': 'payment'
  };
  
  const currentStep = state.steps[state.stepIndex];
  const stepUrl = stepNames[currentStep] || 'holiday';
  
  const url = window.location.href;
  const baseUrl = url.split('?')[0];
  let queryString = url.split('?')[1] || '';
  
  if (queryString) {
    const hasStepParams = /your-(holiday|details|payment)/.test(queryString);
    
    if (hasStepParams) {
      const tourCodeIndex = queryString.indexOf('tourCode=');
      if (tourCodeIndex !== -1) {
        queryString = `your-${stepUrl}&${queryString.substring(tourCodeIndex)}`;
      } else {
        queryString = `your-${stepUrl}`;
      }
    } else {
      queryString = `your-${stepUrl}${queryString ? '&' + queryString : ''}`;
    }

    // Keep first-touch campaign params on the checkout URL across step changes.
    queryString = ensureCampaignParamsOnCheckoutQuery(queryString);
    
    const newUrl = `${baseUrl}?${queryString}`;
    window.history.replaceState({}, '', newUrl);
  } else {
    const queryString = ensureCampaignParamsOnCheckoutQuery(`your-${stepUrl}`);
    const newUrl = `${baseUrl}?${queryString}`;
    window.history.replaceState({}, '', newUrl);
  }
};

const updateCheckoutUrl = (state) => {
  const url = window.location.href;
  const baseUrl = url.split('?')[0];
  const urlParams = new URLSearchParams(window.location.search);
  
  // Update key checkout parameters
  if (state.data.productId) {
    urlParams.set('productId', state.data.productId);
  }
  if (state.quote.departureId) {
    urlParams.set('departureId', state.quote.departureId);
  }
  if (state.quote.tourCode) {
    urlParams.set('tourCode', state.quote.tourCode);
  }
  if (state.quote.departureTransportCode) {
    urlParams.set('transportCode', state.quote.departureTransportCode);
  }
  if (state.quote.filo) {
    urlParams.set('filo', state.quote.filo);
  }
  
  const newUrl = `${baseUrl}?${urlParams.toString()}`;
  window.history.replaceState({}, '', newUrl);
};

export default {
  setIsCheckoutPage(state, isCheckoutPage) {
    state.isCheckoutPage = isCheckoutPage;
  },

  setLoading(state, loading) {
    state.loading = loading;
  },

  setExcursionsLoading(state, excursionsLoading) {
    state.excursionsLoading = excursionsLoading;
  },

  setLoadingStepOverride(state, stepName) {
    state.loadingStepOverride = stepName;
  },

  setPaymentLoading(state, paymentLoading) {
    state.paymentLoading = paymentLoading;
  },

  setPassengerLoading(state, loading) {
    state.passengerLoading = loading;
  },

  setSteps(state, steps) {
    state.steps = steps;
  },

  nextStep(state, stepAmount = 1) {
    state.stepIndex = state.stepIndex + stepAmount;
    state.loadingStepOverride = null;
    updateStepUrl(state);
  },

  prevStep(state, stepAmount = 1) {
    state.stepIndex = state.stepIndex - stepAmount;
    updateStepUrl(state);
  },

  setData(state, data) {
    // Store the original productId if it's the first time setting data
    if (data.productId && !state.data.originalProductId) {
      state.data.originalProductId = data.productId;
    }
    
    state.data = {
      ...state.data,
      ...data
    };
  },

  setQuote(state, quote) {
    state.quote = {
      ...state.quote,
      ...quote
    };
  },

  setQuoteDonations(state) {
    if (state.quote.donations) {
      state.quote.donations.forEach((donation, index) => {
        // Vue 3 compatible: direct assignment works with reactivity
        state.quote.donations[index].continent = state.continent;
        state.quote.donations[index].country = state.country;
        state.quote.donations[index].quantity = state.quote.passengers.filter(p => p.donated).length;
      });
    }
  },

  /** check if the quote.subtotal from API matches our runningSubtotal */
  setSubtotalMismatch(state, runningSubtotal) {
    state.subtotalMismatch = state.quote.subtotal !== runningSubtotal;
  },

  setTourCode(state, tourCode) {
    state.quote.tourCode = tourCode;
    updateCheckoutUrl(state);
  },

  /** Reset the tour code to the initial tour code */
  resetTourCode(state) {
    state.quote.tourCode = state.quote.initialTourCode;
    // Also reset productId to initial value when resetting tour code
    if (state.data.originalProductId) {
      state.data.productId = state.data.originalProductId;
    }
    updateCheckoutUrl(state);
  },

  setTourName(state, tourName) {
    state.data.tourName = tourName;
  },

  setProductId(state, productId) {
    state.data.productId = productId;
    updateCheckoutUrl(state);
  },

  setQuoteUrl(state, quoteUrl) {
    state.quote.quoteUrl = quoteUrl;
  },

  setDepartureId(state, departureId) {
    state.quote.departureId = departureId;
    updateCheckoutUrl(state);
  },

  setDepartureTransportCode(state, departureTransportCode) {
    state.quote.departureTransportCode = departureTransportCode;
  },

  setAddonData(state, addonData) {
    state.addonData = addonData;
  },

  setDepartureDetails(state, departureDetails) {
    state.departureDetails = {
      ...departureDetails
    };
  },

  setAvailableRooms(state, availableRooms) {
    state.availableRooms = availableRooms;
  },

  setMaxiumumPassengers(state, maxAvailable) {
    if (maxAvailable < 9) {
      state.maxPassengers = maxAvailable;
    }
  },


  addRoom(state) {
    state.rooms.push(getRoomModel());
  },

  updateRoom(state, {
    room,
    index
  }) {

    const currentRoom = state.rooms[index];
    const currentPassengers = currentRoom.passengers;
    let newPassengers = [];

    // if room.occupancy is less than the currentRoom.occupancy
    if (room.occupancy < currentRoom.occupancy) {
      // simply modify the passenger array rather than replacing it
      newPassengers = currentPassengers.slice(0, room.occupancy);
    }

    // if room.occupancy is more than the currentRoom.occupancy
    else if (room.occupancy > currentRoom.occupancy) {
      // add new items to the passenger array
      newPassengers = [...currentPassengers];
      for (let i = currentRoom.occupancy; i < room.occupancy; i++) {
        newPassengers.push(getPassengerModel());
      }
    }

    // new room
    else {
      newPassengers =
        currentPassengers.length === room.occupancy
          ? [...currentPassengers]
          : new Array(room.occupancy).fill(null).map(() => getPassengerModel());
    }

    state.rooms[index] = {
      ...room,
      passengers: newPassengers
    };
  },

  removeRoom(state, index) {
    state.rooms.splice(index, 1);
  },

  setRoomPassengerId(state, payload) {
    // Vue 3 compatible: direct assignment works with reactivity
    payload.roomPassenger.passengerId = payload.passengerId;
  },

  excursionsVisited(state) {
    state.excursionsVisited = true;
  },

  excursionsExist(state) {
    state.excursionsExist = true;
  },

  resetExcursions(state) {
    state.excursions = [];
    sessionStorage.removeItem('room-0-passenger-0-excursion');
  },

  invalidateRoomPassengersCache(state) {
    state.roomPassengersCacheBuster++;
  },

  // AB-TEST(room-type-cards) START
  markRoomStepEnterSent(state) {
    state.roomStepEnterSent = true;
  },

  markRoomStepCompleteSent(state) {
    state.roomStepCompleteSent = true;
  },
  // AB-TEST(room-type-cards) END

  togglePassengerExcursion(state, {
    passenger,
    excursion
  }) {
    // TODO: cleanup (we might be able to simplify the data like below)
    /**
     * {
     *  [Excursion Code]: [Passenger Id, Passenger Id]
     * }
     */

    // if excursion exists
    // toggle the selected value of the passenger
    // otherwise, add the excursion and passenger to array
    const excursionIndex = state.excursions.findIndex(e => e.activitiesId === excursion.activitiesId);
    
    if (excursionIndex > -1) {

      const passengerIndex = state.excursions[excursionIndex].passengers.findIndex(p => p.passengerGuid === passenger.passengerGuid);
      
      if (passengerIndex > -1) {
        // toggle passenger
        const pas = state.excursions[excursionIndex].passengers[passengerIndex];
        state.excursions[excursionIndex].passengers[passengerIndex] = {
          ...pas,
          selected: !pas.selected
        };
      } else {
        // add passenger
        // Vue 3 compatible: direct assignment works with reactivity
        state.excursions[excursionIndex].passengers = [
          ...state.excursions[excursionIndex].passengers,
          {
            ...passenger,
            selected: true
          }
        ];
      }
    } else {
      state.excursions.push({
        ...excursion,
        passengers: [{
          ...passenger,
          selected: true
        }]
      });
    }

    const rooms = state.rooms;
    const selections = state.excursions.map(e => ({
      activitiesId: e.activitiesId,
      name: e.name,
      price: e.price,
      positions: e.passengers
        .filter(p => p.selected)
        .map(p => {
          for (let ri = 0; ri < rooms.length; ri++) {
            const pi = rooms[ri].passengers.findIndex(rp => rp.key === p.passengerGuid);
            if (pi !== -1) return `${ri}-${pi}`;
          }
          return null;
        })
        .filter(pos => pos !== null)
    })).filter(e => e.positions.length > 0);
    sessionStorage.setItem('room-0-passenger-0-excursion', JSON.stringify(selections));
  },

  setPassengerResources(state, passengerResources) {
    state.passengerResources = passengerResources;
  },

  setPassengerResourcesLoading(state, loading) {
    state.passengerResourcesLoading = loading;
  },

  setExcursionData(state, excursionData) {
    state.excursionData = excursionData;
    state.hasExcursions = excursionData && excursionData.excursions && excursionData.excursions.length > 0;
  },

  syncExcursionPassengerData(state, roomPassengers) {
    state.excursions.forEach((excursion, exIdx) => {
      const activePassengers = excursion.passengers.filter(excPassenger =>
        roomPassengers.some(rp => rp.passengerGuid === excPassenger.passengerGuid)
      );

      state.excursions[exIdx].passengers = activePassengers.map((excPassenger) => {
        const updatedPassenger = roomPassengers.find(
          rp => rp.passengerGuid === excPassenger.passengerGuid
        );
        return {
          ...excPassenger,
          firstName: updatedPassenger.firstName,
          lastName: updatedPassenger.lastName,
          email: updatedPassenger.email
        };
      });
    });
  },

  setDonationsData(state, donationsData) {
    state.donationsData = donationsData;
  },

  addNonDonatingTraveller(state, nonDonatingTraveller) {
    if (!nonDonatingTraveller || !String(nonDonatingTraveller).trim() || state.nonDonatingTravellers.includes(nonDonatingTraveller)) return;
    state.nonDonatingTravellers.push(nonDonatingTraveller);
    state.nonDonatingTravellers = state.nonDonatingTravellers.slice(); // Force reactivity
  },

  removeNonDonatingTraveller(state, nonDonatingTraveller) {    
    const indexOfElementToRemove = state.nonDonatingTravellers.indexOf(nonDonatingTraveller);
    if (indexOfElementToRemove !== -1) {
      state.nonDonatingTravellers.splice(indexOfElementToRemove, 1);
      state.nonDonatingTravellers = state.nonDonatingTravellers.slice(); // Force reactivity
    }
  },

  setBookingDonation(state, value) {
    if (typeof value === 'boolean') {
      state.donationPerBooking = value;
    } else {
      state.donationPerBooking = !state.donationPerBooking;
    }
    state.donationUpdateTrigger++;
  },

  setDonationUpdateTrigger(state) {
    state.donationUpdateTrigger++;
  },

  setDonationMade(state, donationMade) {
    state.donationMade = donationMade;
  },

  setPassengersFromCustomers(state, roomPassengers) {
    const passengersFromCustomers = [...state.customers];

    const passengersAndCustomers = roomPassengers.map(passenger => {
      const matchedCustomer = passengersFromCustomers.find(customer => 
        customer.accountEmail === passenger.email
      );
    
      return {
        ...passenger,
        ...matchedCustomer
      };
    });


    if (state.loggedInPassengerData) {
      const otherPassengerData = { ...state.loggedInPassengerData };
      delete otherPassengerData.travelCredits;
      delete otherPassengerData.loyalties;

      const passengerIndex = passengersAndCustomers.findIndex(
        ({ firstName, lastName, email, dateOfBirth, lead }) =>
          firstName.toUpperCase() === otherPassengerData.firstName.toUpperCase() &&
          lastName.toUpperCase() === otherPassengerData.lastName.toUpperCase() &&
          email === otherPassengerData.email &&
          dateOfBirth === otherPassengerData.dateOfBirth &&
          lead === true
      );

      if (passengerIndex > -1) {
        passengersAndCustomers[passengerIndex] = {
          ...otherPassengerData,
          ...passengersAndCustomers[passengerIndex]
        };
      }
    }

    state.passengers = passengersAndCustomers;
  },

  setPassengersFromRoomPassengers(state, roomPassengers) {
    const passengersAndCustomers = [...roomPassengers];

    if (state.loggedInPassengerData) {
      const otherPassengerData = { ...state.loggedInPassengerData };
      delete otherPassengerData.travelCredits;
      delete otherPassengerData.loyalties;

      const passengerIndex = passengersAndCustomers.findIndex(
        ({ firstName, lastName, email, dateOfBirth, lead }) =>
          firstName.toUpperCase() === otherPassengerData.firstName.toUpperCase() &&
          lastName.toUpperCase() === otherPassengerData.lastName.toUpperCase() &&
          email === otherPassengerData.email &&
          dateOfBirth === otherPassengerData.dateOfBirth &&
          lead === true
      );

      if (passengerIndex > -1) {
        passengersAndCustomers[passengerIndex] = {
          ...otherPassengerData,
          ...passengersAndCustomers[passengerIndex]
        };
      }
    }

    state.passengers = passengersAndCustomers;
  },

  setloggedInPassengerData(state, userData) {
    state.loggedInPassengerData = userData;
    state.travelCredits = userData?.travelCredits || [];
    state.validatedTravelCredits = [];
    state.loyalties = userData?.loyalties || [];
    // Update the cache when profile data is set
    currentProfileCache.set(userData);
  },

  resetPassengers(state) {
    state.passengers = [];
  },

  setPayment(state, payment) {
    state.payment = payment;
  },

  resetPayment(state) {
    state.paymentLoading = false;
    state.payment = null;
    state.appliedTravelCredits = [];
    state.travelCreditError = '';
  },

  setValidatedTravelCredits(state, credits) {
    state.validatedTravelCredits = credits || [];
  },

  setAppliedTravelCredits(state, credits) {
    state.appliedTravelCredits = credits || [];
  },

  setPaymentFrame(state, paymentFrame) {
    state.paymentFrame = paymentFrame;
  },

  setTransId(state, transId) {
    state.transId = transId;
  },

  setPreviousAmountPaid(state, amount) {
    state.previousAmountPaid = amount;
  },

  setBookingReference(state, bookingReference) {
    state.bookingReference = bookingReference;
  },

  setBooking(state, booking) {
    state.booking = booking;
  },

  setTravelCreditError(state, message) {
    state.travelCreditError = message;
  },

  setBookingId(state, bookingId) {
    state.bookingId = bookingId;
  },

  setBookingType(state, bookingType) {
    state.bookingType = bookingType;
  },

  setBookingData(state, bookingData) {
    state.bookingData = bookingData;
  },

  setCustomers(state, customers) {
    state.customers = customers;
  },

  setDepartureServices(state, departureServices) {
    state.departureServices = departureServices;
  },

  setActivityServices(state, activityServices) {
    state.activityServices = activityServices;
  },

  setFeeServices(state, feeServices) {
    state.feeServices = feeServices;
  },

  setMarketingPreferences(state, marketingPreferences) {
    state.marketingPreferences = marketingPreferences;
    state.marketingPreferencesSubmitted = false;
  },

  setMarketingPreferencesSubmitted(state, submitted) {
    state.marketingPreferencesSubmitted = submitted;
  },

  setError(state, error) {
    state.loading = false;
    state.error = error;
  },

  setErrorMessage(state, errorMessage) {
    state.errorMessage = errorMessage;
  },

  setOnRequestError(state, isOnRequest) {
    state.isOnRequest = isOnRequest;
  },

  setIsLoginBoxDisplayed(state, isLoginBoxDisplayed) {
    state.isLoginBoxDisplayed = isLoginBoxDisplayed;
  },

  toggleTakeFinalBalance(state) {
    state.takeFinalBalance = !state.takeFinalBalance;
  },

  setTakeFinalBalanceValue(state, value) {
    state.takeFinalBalance = value;
  },

  setDataExcursions(state, data) {
    state.excursions.forEach((excursion, index) => {
      const passengersCount = excursion.passengers.length;
      // Vue 3 compatible: direct assignment works with reactivity
      state.excursions[index].continent = data.tourContinent;
      state.excursions[index].country = data.tourCountry;
      state.excursions[index].quantity = passengersCount;
    });

    state.excursions = state.excursions.filter(excursion => {
      return excursion.passengers.some(passenger => passenger.selected);
    });
  },

  setCreatedActivities(state, data) {
    state.activities = data;
  },

  setRoomsData(state, data) {
    state.rooms.forEach((room, index) => {
      // Vue 3 compatible: direct assignment works with reactivity
      state.rooms[index].continent = data.tourContinent;
      state.rooms[index].country = data.tourCountry;
      state.rooms[index].quantity = room.occupancy;
      state.rooms[index].price = room.pricePerPerson;
      state.rooms[index].variant = room.name;
    });
  },

  setPaymentType(state, paymentType) {
    state.paymentType = paymentType;
  },
 
  setTransactionType(state, transactionType) {
    state.transactionType = transactionType;
  },

  setToursData(state) {
    const newTour = {
      brand: state.data.brand,
      continent: state.data.tourContinent,
      country: state.data.tourCountry,
      discount: state.data.discount,
      quantity: 1,
      price: state.data.price,
      id: state.quote.tourCode,
      name: state.data.tourName
    };
    state.tours.push(newTour);
  },

  setDonations(state) {
    if (!state.donationsData) return;

    const passengers = state.quote.passengers || [];
    const quantity = passengers.filter(passenger => passenger.donated).length;
    if (!quantity) return;

    const donationId =
      state.donationsData.id ??
      state.donationsData.feeId ??
      state.quote.donationId ??
      (Array.isArray(state.quote.donationIds) ? state.quote.donationIds[0] : undefined);

    if (donationId == null) return;

    state.donations.push({
      brand: state.donationsData.brandName,
      id: donationId,
      name: state.donationsData.name,
      price: state.donationsData.price,
      quantity
    });
  },
  
  setAddons(state) {
    const newAddons = state.addonData.addOns.map(addOn => ({
      continent: state.data.tourContinent,
      country: state.data.tourCountry,
      name: addOn.name,
      price: addOn.price,
      id: addOn.code
    }));
    
    state.addons.push(...newAddons);
  },

  setFlights(state) {
    const flights = state.departureDetails;
    
    const mapLegsToFlight = (legs) => {
      return legs.map(leg => ({
        brand: leg.flightCarrier.name,
        name: `flights(${leg.departureAirport.code}/${leg.arrivalAirport.code})`,
        id: leg.instanceCode,
        quantity: state.quote.passengers.length
      }));
    };

    const departureFlights = mapLegsToFlight(flights.departureLegs);
    const returnFlights = mapLegsToFlight(flights.returnLegs);

    state.flights = [...departureFlights, ...returnFlights];
  },

  setRoomsError(state, value) {
    state.setRoomsError = value;
  },

  setAmountSelected(state, amount) {
    state.amountSelected = amount;
  },

  setBookingFlights(state) {
    const flights = state.departureDetails;
    const mapLegsToFlight = (legs) => {
      return (legs || []).map(leg => ({
        airline: leg.flightCarrier.name,
        carrierCode: leg.instanceCode, // unique per flight
        departureAirport: leg.departureAirport.name,
        arrivalAirport: leg.arrivalAirport.name,
        departureDate: leg.departureTime.date,
        departureTime: leg.departureTime.time,
        arrivalDate: leg.arrivalTime.date,
        arrivalTime: leg.arrivalTime.time,
      }));
    };
    const departureFlights = mapLegsToFlight(flights.departureLegs);
    const returnFlights = mapLegsToFlight(flights.returnLegs);
    state.bookingFlights = [...departureFlights, ...returnFlights];
  },

  setPaymentCardType(state, paymentCardType) {
    state.paymentCardType = paymentCardType;
  },

  setFailedExcursions(state, failedExcursions) {
    state.failedExcursions = failedExcursions;
  },

  updatePassengerDirectData(state, { passengerGuid, data }) {
    const index = state.passengers.findIndex(p => p.passengerGuid === passengerGuid);
    if (index > -1) {
      Object.assign(state.passengers[index], data);
    }
  },

  clearBookingValidationFeedback(state) {
    const clearFields = form => {
      if (!form?.fields) {
        return;
      }
      Object.keys(form.fields).forEach(key => {
        const f = form.fields[key];
        if (!f) {
          return;
        }
        form.fields[key] = {
          ...f,
          error: false,
          message: '',
          serverBookingError: false
        };
      });
    };
    (state.passengers || []).forEach(p => {
      clearFields(p);
      if (p.address) {
        clearFields(p.address);
        if (p.address.fields?.addressLines) {
          clearFields(p.address.fields.addressLines);
        }
      }
    });
    Object.values(state.contactModal || {}).forEach(clearFields);
    state.bookingValidationContactModalIndexes = [];
  },

  bookingValidationPromptContactModal(state, passengerIndexes) {
    const list = Array.isArray(passengerIndexes) ? passengerIndexes : [passengerIndexes];
    const unique = [...new Set(list)].filter(idx => typeof idx === 'number' && !Number.isNaN(idx));
    if (!unique.length) {
      return;
    }
    state.bookingValidationContactModalBump += 1;
    state.bookingValidationContactModalIndexes = unique;
  },

  clearServerBookingStateOnPassengerFields(state, { passengerIndex, fieldNames }) {
    const form = state.passengers?.[passengerIndex];
    if (!form?.fields) {
      return;
    }
    const list = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
    list.forEach(name => {
      const f = form.fields[name];
      if (!f) {
        return;
      }
      form.fields[name] = {
        ...f,
        error: false,
        message: '',
        serverBookingError: false
      };
    });
  },

  clearAddressFieldsServerBookingState(state, { vuexPath, fieldNames }) {
    const path = Array.isArray(vuexPath) ? [...vuexPath] : String(vuexPath || '').split('/').filter(Boolean);
    const relativePath = path[0] === 'checkout' ? path.slice(1) : path;
    const form = getNestedStateObject(state, relativePath);
    if (!form?.fields) {
      return;
    }
    const list = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
    list.forEach(name => {
      const f = form.fields[name];
      if (!f) {
        return;
      }
      form.fields[name] = {
        ...f,
        error: false,
        message: '',
        serverBookingError: false
      };
    });
  },

  reopenPassengerDetailEditors(state, passengerIndexes) {
    const list = Array.isArray(passengerIndexes) ? passengerIndexes : [passengerIndexes];
    list.forEach(idx => {
      if (typeof idx !== 'number' || idx < 0 || idx >= state.passengers.length) {
        return;
      }
      const p = state.passengers[idx];
      p.summaryView = false;
      p.hasBeenSubmitted = false;
    });
  },

  setPassengerFieldApiMessage(state, { passengerIndex, fieldName, message, fallbackValue = '' }) {
    const msg = message || '';
    const passenger = state.passengers[passengerIndex];
    if (!passenger) {
      return;
    }

    /** Fields under `passenger.address` (AddressLookup / AutocompleteInput for county, etc.). */
    const addressRootFields = new Set(['county', 'town', 'postCode', 'country']);
    if (addressRootFields.has(fieldName)) {
      if (!passenger.address) {
        passenger.address = {
          summaryView: false,
          hasBeenSubmitted: false,
          fields: {}
        };
      }
      const addressSrc = passenger.address.fields?.[fieldName];
      const applyServerErrorToAddressForm = formRoot => {
        if (!formRoot) {
          return;
        }
        if (!formRoot.fields) {
          formRoot.fields = {};
        }
        if (!formRoot.fields[fieldName]) {
          if (addressSrc) {
            formRoot.fields[fieldName] = {
              value: addressSrc.value,
              error: addressSrc.error,
              message: '',
              userHasTyped: addressSrc.userHasTyped ?? false,
              customValidation: addressSrc.customValidation ?? null
            };
          } else {
            formRoot.fields[fieldName] = {
              value: fallbackValue || '',
              error: false,
              message: '',
              userHasTyped: false,
              customValidation: null
            };
          }
        }
        const field = formRoot.fields[fieldName];
        formRoot.fields[fieldName] = {
          ...field,
          error: true,
          message: msg,
          userHasTyped: true,
          serverBookingError: true
        };
      };
      applyServerErrorToAddressForm(passenger.address);
      return;
    }

    const src = passenger?.fields?.[fieldName];

    if (!state.contactModal[passengerIndex]) {
      state.contactModal[passengerIndex] = {
        summaryView: false,
        hasBeenSubmitted: false,
        fields: {}
      };
    }

    const applyServerErrorToForm = formRoot => {
      if (!formRoot) {
        return;
      }
      if (!formRoot.fields) {
        formRoot.fields = {};
      }
      if (!formRoot.fields[fieldName]) {
        if (src) {
          formRoot.fields[fieldName] = {
            value: src.value,
            error: src.error,
            message: '',
            userHasTyped: src.userHasTyped ?? false,
            customValidation: src.customValidation ?? null
          };
        } else {
          formRoot.fields[fieldName] = {
            value: fallbackValue || '',
            error: false,
            message: '',
            userHasTyped: false,
            customValidation: null
          };
        }
      }
      const field = formRoot.fields[fieldName];
      formRoot.fields[fieldName] = {
        ...field,
        error: true,
        message: msg,
        userHasTyped: true,
        serverBookingError: true
      };
    };

    applyServerErrorToForm(passenger);
    applyServerErrorToForm(state.contactModal[passengerIndex]);
  }
}
